// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';

import { UserDO } from 'wdna-components';
import { RowDataChangedEvent } from 'wdna-grid-table/lib/types';
import Table, { TableButtonsConfig } from 'wdna-components/lib/table/Table';
import { TabConfig, addTab, removeTab } from 'wdna-components/lib/tabs';
import { messageInfo, messageError, messageBoxWarning } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import AdjustmentsEdit from '../forms/adjustments/Edit';

import { adjustment, AdjustmentListDO, sensor, SensorListDO } from '../../requests';
import { typeSensor, TypeSensorListDO } from '../../../configuration/requests';
import { company, CompanyDO } from '../../../management/requests';

type Props = unknown;

interface State {
  loading: boolean;
  update: string;
  showGrid: boolean;

  data: any[];
}

export default class Adjustments extends React.Component<Props, State> {
  private tabId: string;
  private buttons: TableButtonsConfig;
  private mounted: boolean;

  public constructor(props: Props) {
    super(props);

    this.tabId = window.getStr();
    this.mounted = false;
    this.buttons = {
      edit: true,
      add: true,
      del: true,
      toggleItem: true,
    };

    this.state = {
      loading: false,
      update: window.getStr(),
      showGrid: false,

      data: [],
    };
  }

  public componentDidMount = (): void => {
    this.mounted = true;
    this.load().catch(() => { /* pass */ });
  };

  public shouldComponentUpdate = (nextProps: Props, nextState: State): boolean => {
    return this.state.update !== nextState.update;
  }

  public componentWillUnmount = (): void => {
    this.mounted = false;
  };

  public load = async (): Promise<void> => {
    this.loadingStart();
    try {
      const [companyArray, sensorArray, typeSensorArray, adjustmentTable] = await Promise.all([
        company<CompanyDO[]>('list') || [],
        sensor<SensorListDO[]>('list') || [],
        typeSensor<TypeSensorListDO[]>('list') || [],
        adjustment<AdjustmentListDO[]>('list') || [],
      ]);
      if (this.mounted) {
        this.setState({
          data: this.addExtraColumns(adjustmentTable, companyArray, sensorArray, typeSensorArray),
        });
      }
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public addExtraColumns = (data: AdjustmentListDO[] = [], companyArray: CompanyDO[], sensorArray: SensorListDO[], typeSensorArray: TypeSensorListDO[]): any[] => {
    const user: UserDO = _.get(window.store.getState(), ['user', 'user']);
    const userIsAdmin: boolean = (user.type === 'adm');

    // Admins can see everything, normal users only their company
    const filteredData: Record<string, any>[] = data.filter((item1: Record<string, any>) => {
      const selectedSensor: SensorListDO | undefined = sensorArray.find((item2: SensorListDO) => item2.id === +item1.sensorId);
      const sensorText: string = selectedSensor ? `${selectedSensor.code} | ${selectedSensor.name}` : 'unknown';
      item1.sensorName = sensorText;

      const selectedSensorType: TypeSensorListDO | undefined = typeSensorArray.find((item2: TypeSensorListDO) => item2.id === +item1.typeSensorId);
      const typeSensorText: string = selectedSensorType ? `${selectedSensorType.name} (${selectedSensorType.typeModuleName})` : 'unknown';
      item1.sensorType = typeSensorText;

      const selectedCompany: CompanyDO | undefined = companyArray.find((item2: CompanyDO) => item2.id === +(selectedSensor?.companyId || 0));
      item1.companyName = selectedCompany?.name;

      return userIsAdmin;
    });

    return filteredData.map((item: Record<string, any>): Record<string, any> => {
      // Reorder the columns
      return {
        id: item.id,
        sensorName: item.sensorName,
        sensorType: item.sensorType,
        companyName: item.companyName,
        variableName: item.variableName,
        formula: item.formula,
        ...item,
      };
    });
  };

  public loadingStart = (): void => {
    this.setState({
      update: window.getStr(),
      loading: true,
    });
  };

  public loadingFinish = (): void => {
    this.setState({
      update: window.getStr(),
      loading: false,
    });
  };

  public loadTab = (rowId: number): void => {
    const tabId: string = 'AD';
    const tabData: TabConfig = {
      id: tabId,
      active: true,
      title: 'Adjustments Edit',
      component: AdjustmentsEdit,
      props: {
        rowId,
        tabId: this.tabId,
        report: 'ADJUSTMENTS',
        title: 'Adjustments',
        notify: this.notify,
      },
    };
    window.store.dispatch(addTab(tabData));
  };

  public notify = (): void => {
    this.load().catch(() => { /* pass */ });
  };

  public onEdit = (value: AdjustmentListDO): void => {
    window.store.dispatch(removeTab('AD'));
    const rowId: number = value.id;
    setTimeout(() => {
      this.loadTab(rowId);
    }, 0);
  };

  public onDelete = (value: AdjustmentListDO): void => {
    messageBoxWarning('Delete', 'Do you want to delete this register?', () => {
      this.loadingStart();
      adjustment('del', String(value.id))
        ?.then(() => {
          messageInfo('Success', 'Register successfully deleted', 5000);
          this.load().catch(() => { /* pass */ });
        })
        .catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        })
        .finally(() => {
          this.loadingFinish();
        });
    });
  };

  public rowDataChanged = (_event: RowDataChangedEvent): void => {
    this.setState({
      update: window.getStr(),
      showGrid: true,
    });
  };

  public render(): JSX.Element {
    const { loading, data, showGrid } = this.state;

    const columnAlias: Record<string, string> = {
      id: 'ID',
      sensorName: 'Sensor',
      sensorType: 'Sensor type',
      variableName: 'Variable',
      companyName: 'Company',
      formula: 'Formula',
      comments: 'Comments',
    };
    const columnsToHide: string[] = [
      'sensorId',
      'variableId',
      'typeSensorId',
      'typeSensorName',
      'typeSensorVariableId',
      'typeSensorVariableFormula',
      'sensorId',
    ];
    const columnStyles: Record<string, any> = {
      id: { textAlign: 'right' },
      sensorName: { textAlign: 'left' },
      sensorType: { textAlign: 'left' },
      variableName: { textAlign: 'left' },
      companyName: { textAlign: 'left' },
      formula: { textAlign: 'left' },
      comments: { textAlign: 'left' },
      actionButtons: { textAlign: 'center' },
    };

    return (
      <div id="content" className="content" style={{ visibility: (showGrid ? 'visible' : 'hidden') }}>
        <Table
          data={data || []}
          alias={columnAlias}
          customCellStyles={columnStyles}
          hiddenCols={columnsToHide}
          buttons={this.buttons}
          onEdit={this.onEdit}
          onDelete={this.onDelete}
          onAdd={this.onEdit}
          loading={loading}
          rowDataChanged={this.rowDataChanged}
        />
      </div>
    );
  }
}
